import React, { useState, useEffect } from 'react';
import {
    Table, Button, Input, Badge, Spinner,
    Modal, ModalHeader, ModalBody, ModalFooter,
    FormGroup, Label
} from 'reactstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import languageService from 'services/language.service';

function LanguageProposals({ tool }) {
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [selectedIds, setSelectedIds] = useState([]);
    const [editModal, setEditModal] = useState(false);
    const [editItem, setEditItem] = useState(null);

    useEffect(() => {
        if (tool) fetchProposals();
    }, [tool, statusFilter]);

    async function fetchProposals() {
        setLoading(true);
        try {
            const res = await languageService.getProposals(tool, statusFilter, 1, 200);
            if (res.data?.success) {
                setProposals(res.data.data);
            }
        } catch (err) {
            console.error('Error fetching proposals:', err);
        } finally {
            setLoading(false);
        }
    }

    async function handleApprove(item) {
        try {
            const res = await languageService.approveProposal(item.id);
            if (res.data?.success) {
                Swal.fire({ icon: 'success', title: 'Đã duyệt!', text: `Key "${item.key}" đã thêm vào languages`, timer: 2000, showConfirmButton: false });
                fetchProposals();
            }
        } catch (err) {
            Swal.fire('Lỗi', err.message, 'error');
        }
    }

    async function handleReject(item) {
        const { value: note } = await Swal.fire({
            title: `Từ chối "${item.key}"`,
            input: 'textarea',
            inputLabel: 'Lý do (tuỳ chọn)',
            inputPlaceholder: 'Ghi lý do...',
            showCancelButton: true,
            confirmButtonText: 'Từ chối',
            cancelButtonText: 'Huỷ',
            confirmButtonColor: '#e74c3c'
        });

        if (note !== undefined) {
            try {
                await languageService.rejectProposal(item.id, { reviewNote: note });
                Swal.fire({ icon: 'info', title: 'Đã từ chối', timer: 1500, showConfirmButton: false });
                fetchProposals();
            } catch (err) {
                Swal.fire('Lỗi', err.message, 'error');
            }
        }
    }

    async function handleBulkApprove() {
        if (selectedIds.length === 0) return;
        const result = await Swal.fire({
            title: `Duyệt ${selectedIds.length} mục?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Duyệt tất cả',
            cancelButtonText: 'Huỷ'
        });
        if (result.isConfirmed) {
            try {
                const res = await languageService.bulkApproveProposals(selectedIds);
                Swal.fire({ icon: 'success', title: res.data.message, timer: 2500, showConfirmButton: false });
                setSelectedIds([]);
                fetchProposals();
            } catch (err) {
                Swal.fire('Lỗi', err.message, 'error');
            }
        }
    }

    async function handleDelete(item) {
        const result = await Swal.fire({
            title: `Xoá proposal "${item.key}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Xoá',
            cancelButtonText: 'Huỷ',
            confirmButtonColor: '#e74c3c'
        });
        if (result.isConfirmed) {
            try {
                await languageService.deleteProposal(item.id);
                fetchProposals();
            } catch (err) {
                Swal.fire('Lỗi', err.message, 'error');
            }
        }
    }

    function openEdit(item) {
        setEditItem({ ...item });
        setEditModal(true);
    }

    async function saveEdit() {
        try {
            await languageService.approveProposal(editItem.id, {
                vi: editItem.vi,
                en: editItem.en,
                reviewNote: 'Sửa bởi admin trước khi duyệt'
            });
            Swal.fire({ icon: 'success', title: 'Đã sửa & duyệt!', timer: 2000, showConfirmButton: false });
            setEditModal(false);
            fetchProposals();
        } catch (err) {
            Swal.fire('Lỗi', err.message, 'error');
        }
    }

    function toggleSelect(id) {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    }

    function toggleSelectAll() {
        if (selectedIds.length === proposals.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(proposals.map(p => p.id));
        }
    }

    const statusBadge = (s) => {
        if (s === 'approved') return <Badge color="success" style={{ fontSize: '11px', borderRadius: '12px', padding: '4px 10px' }}>✅ Đã duyệt</Badge>;
        if (s === 'rejected') return <Badge color="danger" style={{ fontSize: '11px', borderRadius: '12px', padding: '4px 10px' }}>❌ Từ chối</Badge>;
        return <Badge color="warning" style={{ fontSize: '11px', borderRadius: '12px', padding: '4px 10px' }}>🟡 Đang chờ</Badge>;
    };

    return (
        <div>
            {/* Toolbar */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Input type="select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    style={{ width: '180px', borderRadius: '12px', fontSize: '13px', border: '1.5px solid #e0e0e0' }}>
                    <option value="pending">🟡 Đang chờ duyệt</option>
                    <option value="approved">✅ Đã duyệt</option>
                    <option value="rejected">❌ Đã từ chối</option>
                    <option value="">📋 Tất cả</option>
                </Input>

                {statusFilter === 'pending' && selectedIds.length > 0 && (
                    <Button color="success" size="sm" onClick={handleBulkApprove}
                        style={{ borderRadius: '12px', fontSize: '12px', fontWeight: '600', padding: '6px 14px' }}>
                        ✅ Duyệt {selectedIds.length} mục đã chọn
                    </Button>
                )}

                <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#8898aa' }}>
                    {proposals.length} kết quả
                </span>
            </div>

            {/* Content */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spinner color="primary" />
                    <div style={{ marginTop: '10px', color: '#8898aa', fontSize: '13px' }}>Đang tải...</div>
                </div>
            ) : proposals.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#8898aa' }}>
                    <i className="fas fa-inbox" style={{ fontSize: '40px', marginBottom: '12px', display: 'block', opacity: 0.3 }}></i>
                    <div style={{ fontSize: '14px' }}>Không có đề xuất nào</div>
                </div>
            ) : (
                <Table hover style={{ tableLayout: 'fixed', borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                            {statusFilter === 'pending' && (
                                <th style={{ width: '35px', border: 'none', padding: '8px' }}>
                                    <input type="checkbox"
                                        checked={selectedIds.length === proposals.length && proposals.length > 0}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                            )}
                            <th style={{ border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase', padding: '8px 12px' }}>Key</th>
                            <th style={{ border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase', padding: '8px 12px' }}>Vietnamese</th>
                            <th style={{ border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase', padding: '8px 12px' }}>English</th>
                            <th style={{ border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase', padding: '8px 12px', width: '120px' }}>Context</th>
                            <th style={{ border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase', padding: '8px 12px', width: '100px' }}>Status</th>
                            <th style={{ border: 'none', fontSize: '13px', color: '#8898aa', fontWeight: '600', textTransform: 'uppercase', padding: '8px 12px', width: '150px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {proposals.map(item => (
                            <tr key={item.id} style={{ transition: 'background-color 0.1s' }}>
                                {statusFilter === 'pending' && (
                                    <td style={{ padding: '8px' }}>
                                        <input type="checkbox"
                                            checked={selectedIds.includes(item.id)}
                                            onChange={() => toggleSelect(item.id)}
                                        />
                                    </td>
                                )}
                                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <code style={{ fontSize: '12px', backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>{item.key}</code>
                                </td>
                                <td style={{ padding: '8px 12px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.vi}</td>
                                <td style={{ padding: '8px 12px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.en}</td>
                                <td style={{ padding: '8px 12px', fontSize: '11px', color: '#8898aa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.context}</td>
                                <td style={{ padding: '8px 12px' }}>{statusBadge(item.status)}</td>
                                <td style={{ padding: '8px 12px' }}>
                                    {item.status === 'pending' && (
                                        <>
                                            <Button color="success" size="sm" onClick={() => handleApprove(item)}
                                                title="Duyệt"
                                                style={{ borderRadius: '8px', fontSize: '11px', marginRight: '3px', padding: '3px 8px' }}>
                                                ✅
                                            </Button>
                                            <Button color="info" size="sm" onClick={() => openEdit(item)}
                                                title="Sửa & Duyệt"
                                                style={{ borderRadius: '8px', fontSize: '11px', marginRight: '3px', padding: '3px 8px' }}>
                                                ✏️
                                            </Button>
                                            <Button color="danger" size="sm" onClick={() => handleReject(item)}
                                                title="Từ chối"
                                                style={{ borderRadius: '8px', fontSize: '11px', marginRight: '3px', padding: '3px 8px' }}>
                                                ❌
                                            </Button>
                                        </>
                                    )}
                                    <Button color="secondary" size="sm" onClick={() => handleDelete(item)}
                                        title="Xoá"
                                        style={{ borderRadius: '8px', fontSize: '11px', padding: '3px 8px' }}>
                                        🗑
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {/* Edit + Approve Modal */}
            <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg" centered>
                <ModalHeader toggle={() => setEditModal(false)} style={{ background: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)', color: '#fff', borderBottom: 'none', borderRadius: '12px 12px 0 0' }}>
                    <span style={{ fontSize: '16px' }}>✏️ Sửa & Duyệt: <code style={{ color: '#1abc9c' }}>{editItem?.key}</code></span>
                </ModalHeader>
                <ModalBody style={{ padding: '24px' }}>
                    {editItem && (
                        <>
                            <FormGroup>
                                <Label style={{ fontSize: '13px', fontWeight: '600', color: '#525f7f' }}>Key</Label>
                                <Input value={editItem.key} disabled style={{ borderRadius: '12px', fontSize: '13px', backgroundColor: '#f8f9fa' }} />
                            </FormGroup>
                            <FormGroup>
                                <Label style={{ fontSize: '13px', fontWeight: '600', color: '#525f7f' }}>🇻🇳 Vietnamese</Label>
                                <Input type="textarea" rows="3" value={editItem.vi}
                                    onChange={e => setEditItem({ ...editItem, vi: e.target.value })}
                                    style={{ borderRadius: '12px', fontSize: '13px' }}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label style={{ fontSize: '13px', fontWeight: '600', color: '#525f7f' }}>🇬🇧 English</Label>
                                <Input type="textarea" rows="3" value={editItem.en}
                                    onChange={e => setEditItem({ ...editItem, en: e.target.value })}
                                    style={{ borderRadius: '12px', fontSize: '13px' }}
                                />
                            </FormGroup>
                            {editItem.context && (
                                <div style={{ fontSize: '12px', color: '#8898aa', marginTop: '8px' }}>
                                    📍 Context: {editItem.context}
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter style={{ borderTop: '1px solid #f0f0f0' }}>
                    <Button color="secondary" onClick={() => setEditModal(false)} style={{ borderRadius: '12px', fontSize: '13px' }}>Huỷ</Button>
                    <Button color="success" onClick={saveEdit} style={{ borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>
                        ✅ Sửa & Duyệt
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}

export default LanguageProposals;
